import { TextField, TextFieldProps, Grid, Chip, Autocomplete } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AutocompleteChangeReason } from '@mui/material/useAutocomplete';
import useTranslate from 'Hooks/useTranslate';
import { useState } from 'react';

interface ITextInputDomains extends Omit<TextFieldProps, 'name' | 'onChange'> {
  value: string[];
  onChange(domains: string[]): void;
}

const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

const verifyDomain = (domain: string) => DOMAIN_REGEX.test(domain);

const useStyles = makeStyles(() => ({
  input: {
    maxHeight: 280,
    alignItems: 'start',
    overflowX: 'hidden',
    overflowY: 'auto'
  }
}));

const TextInputDomains = ({ value, onChange, helperText, error, ...otherProps }: ITextInputDomains) => {
  const classes = useStyles();
  const ts = useTranslate();
  const [touched, setTouched] = useState(false);

  const hasInvalidDomain = value.some(d => !verifyDomain(d));
  const showError = touched && hasInvalidDomain;

  const handleChange = (
    _e: React.ChangeEvent<{}>,
    newValues: any[],
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'removeOption' || reason === 'createOption')
      onChange(newValues);
    if (reason === 'clear') onChange([]);
  };

  const handleInputChange = (_e: React.ChangeEvent<{}>, inputValue: string) => {
    const domains = inputValue.split(/[,;\s]/).map(d => d.trim());
    const domainsFiltered = domains.filter(d => !!d);

    if (domains.length > 1 && domainsFiltered.length > 0) {
      onChange([...value, ...domainsFiltered]);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    const domains = e.target.value.split(/[,;\s]/).map(d => d.trim());
    const domainsFiltered = domains.filter(d => !!d);
    if (domainsFiltered.length > 0) {
      onChange([...value, ...domainsFiltered]);
    }
  };

  return (
    <Grid
      container
      direction="column"
      style={{ position: 'relative', paddingBottom: 20 }}
    >
      <Autocomplete
        clearOnBlur
        clearOnEscape
        multiple
        value={value}
        options={[]}
        freeSolo
        limitTags={3}
        renderTags={(tagValues: readonly string[], getTagProps) =>
          tagValues.map((option, index) => (
            <Chip
              variant="outlined"
              color={verifyDomain(option) ? 'default' : 'secondary'}
              label={option}
              {...getTagProps({ index })}
            />
          ))
        }
        onChange={handleChange}
        onInputChange={handleInputChange}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            onBlur={handleBlur}
            placeholder={
              value.length
                ? undefined
                : ts('component.form.placeholder.domainsInput')
            }
            helperText={
              showError
                ? ts('component.modals.createOrganization.errors.invalidDomain')
                : helperText
            }
            inputProps={{
              ...params.inputProps,
              style: {
                overflow: 'auto',
                flex: '0 0 90%'
              }
            }}
            InputProps={{
              ...params.InputProps,
              className: `${params.InputProps.className} ${classes.input}`,
              startAdornment: (
                <div
                  style={{
                    maxHeight: 150,
                    overflowY: 'auto'
                  }}
                >
                  {params.InputProps.startAdornment}
                </div>
              )
            }}
            error={error || showError}
            {...otherProps}
          />
        )}
      />
    </Grid>
  );
};

export default TextInputDomains;
